import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, ArrowLeft } from "lucide-react";
import { PageHeader, Reveal, SectionHeading } from "@/components/public/ui";
import { usePublic } from "@/hooks/usePublic";
import { mediaUrl } from "@/lib/media";

const DEFAULT = {
  slug: "helmet-multigear-sanitization-station",
  name: "Helmet Multigear Sanitization Station",
  short_desc: "360° protection with UV + Heat + Air cleaning.",
  description:
    "Aimway's flagship station provides 360° protection. Powerful UV rays, controlled heat and an air-cleaning system work together to thoroughly sanitize helmets and protective gear. Simply scan the QR code, insert your helmet, and the automatic, chemical-free cleaning process begins — validated in real time by AI.",
  image: "/aimway-logo.png",
  features: [
    "UV-C sterilization kills 99.9% of germs",
    "Controlled heat removes sweat & odour",
    "HEPA-grade air cleaning",
    "AI + RTM real-time cycle monitoring",
    "Chemical-free & helmet-safe",
    "Self-serve QR-based operation",
  ],
  specs: [
    { label: "Model", value: "HS-Pro-2026" },
    { label: "Power", value: "12 W" },
    { label: "Voltage", value: "220 V AC" },
    { label: "Cycle Time", value: "30–60 s" },
    { label: "Weight", value: "2.5 kg" },
    { label: "Dimensions", value: "30 × 25 × 40 cm" },
    { label: "Warranty", value: "1 Year" },
    { label: "Chemicals", value: "None" },
  ],
};

export default function ProductDetail() {
  const { slug } = useParams();
  const { data, loading } = usePublic("/products");
  const list = data || [];
  const product = list.find((p) => p.slug === slug || p.id === slug) || DEFAULT;

  const features = Array.isArray(product.features) ? product.features : DEFAULT.features;
  const specs = Array.isArray(product.specs) && product.specs.length ? product.specs : DEFAULT.specs;

  return (
    <div data-testid="product-detail-page">
      <PageHeader eyebrow="Product" title={product.name} subtitle={product.short_desc} />
      <section className="container-page py-16">
        <Link to="/products" className="mb-8 inline-flex items-center gap-2 text-sm font-600 text-muted-foreground hover:text-brand"><ArrowLeft size={16} /> Back to products</Link>
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-slate-100 bg-brand-sky p-8 shadow-card">
              <img src={mediaUrl(product.image) || "/aimway-logo.png"} alt={product.name} className="mx-auto w-full max-w-sm" data-testid="detail-img" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-muted-foreground leading-relaxed">{product.description || DEFAULT.description}</p>
            <h3 className="mt-8 font-display text-lg font-700 text-brand-navy">Key Features</h3>
            <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-sky text-brand"><Check size={12} /></span>{f}
                </li>
              ))}
            </ul>
            <Link to="/contact" className="mt-9 inline-flex items-center gap-2 rounded-full bg-brand-orange px-7 py-3.5 font-display font-700 text-white transition-colors hover:bg-brand-orange-dark" data-testid="detail-enquire">Request a Quote <ArrowRight size={18} /></Link>
          </Reveal>
        </div>

        <div className="mt-20">
          <SectionHeading eyebrow="Specifications" title="Technical details" className="mb-8" />
          <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-slate-200 sm:grid-cols-4">
            {specs.map((s, i) => (
              <div key={i} className="border-b border-r border-slate-200 p-6">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{s.label}</div>
                <div className="mt-1.5 font-display text-lg font-700 text-brand-navy">{s.value}</div>
              </div>
            ))}
          </div>
        </div>
        {loading && null}
      </section>
    </div>
  );
}
